import { styled } from '@mui/material/styles';
import { Box, Container, Typography, Grid, Paper, Button, Skeleton } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { fadeUp, staggerContainer, reducedMotionVariants } from '../../../theme/animations';
import { SectionTitle, SectionSubtitle } from './SectionHeader';
import { usePopularCities } from '../../locations/hooks/useLocations';

const LocationsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const CityCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    borderColor: theme.palette.primary.main,
  },
}));

const CityName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 600,
  fontSize: '1.125rem',
  color: theme.palette.text.primary,
  flex: 1,
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '1rem',
  marginTop: theme.spacing(6),
  padding: '14px 32px',
  borderRadius: 12,
}));

// Skeleton count to show while loading
const SKELETON_COUNT = 8;

export function LocationPickerSection() {
  const navigate = useNavigate();
  const { data: cities, isLoading } = usePopularCities();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const handleCityClick = (citySlug: string) => {
    navigate(`/discover/gaming/${citySlug}`);
  };

  const handleViewAllClick = () => {
    navigate('/venues');
  };

  return (
    <LocationsContainer>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={itemVariants}>
            <SectionTitle>Browse by Location</SectionTitle>
          </motion.div>

          <motion.div variants={itemVariants}>
            <SectionSubtitle>
              Find venues in your city or explore popular locations across India
            </SectionSubtitle>
          </motion.div>

          <Grid container spacing={3}>
            {isLoading ? (
              Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                  <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
                </Grid>
              ))
            ) : cities && cities.length > 0 ? (
              cities.map((city) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={city.slug}>
                  <motion.div variants={itemVariants}>
                    <CityCard elevation={0} onClick={() => handleCityClick(city.slug)}>
                      <LocationOnIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      <CityName>{city.name}</CityName>
                    </CityCard>
                  </motion.div>
                </Grid>
              ))
            ) : (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 4,
                    borderRadius: 3,
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={(t) => ({
                      fontFamily: t.typography.fontFamilyMono,
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      color: 'text.primary',
                      mb: 1,
                    })}
                  >
                    Expanding to new cities
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    We're onboarding venues across India. Check back soon or help us grow.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div variants={itemVariants}>
              <ViewAllButton variant="outlined" size="large" onClick={handleViewAllClick}>
                Browse All Venues
              </ViewAllButton>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </LocationsContainer>
  );
}
